import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { StudentDirectApplicationService } from '../../../services/student-direct-application/student-direct-application.service';
import { InstituteService } from '../../../services/institute/institute.service';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../models/enums/courseLevelEnum';
import { ApplicationHistoryDialogComponent } from '../../../operations-portal/student/student-details/applications-tab/application-history-dialog/application-history-dialog.component';

interface StudentApplicationRow {
  id: number;
  instituteId: string;
  instituteName: string;
  courseLevel?: number | null;
  instituteLogo?: string | null;
  courseLevelName?: string | null;
  courseName?: string | null;
  countryName?: string | null;
  inTakeYear?: string | null;
  status: number;
  offerStatus?: number | null;
  milestones?: any[];
}

@Component({
  selector: 'app-dashboard-applications',
  standalone: true,
  imports: [CommonModule, TableModule, TagModule, ProgressSpinnerModule, SkeletonModule, RouterModule, ApplicationHistoryDialogComponent],
  templateUrl: './dashboard-applications.component.html',
  styleUrl: './dashboard-applications.component.scss'
})
export class DashboardApplicationsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() studentId: string = '';
  @Output() applicationsLoaded = new EventEmitter<number>();

  @ViewChild('historyDialog') historyDialog!: ApplicationHistoryDialogComponent;

  // Rows to render as skeleton placeholders while loading.
  skeletonRows = Array.from({ length: 5 });

  applications: StudentApplicationRow[] = [];
  isLoading = false;

  // Selected phase tab (1-4). The table is filtered to applications in this phase.
  activePhase = 1;

  private destroy$ = new Subject<void>();

  // Status labels are loaded from the same API the operations portal uses, so the
  // label a student sees always matches the status set by the operations team.
  private statusLabelMap = new Map<number, string>();
  private offerLabelMap = new Map<number, string>();

  constructor(
    private studentDirectApplicationService: StudentDirectApplicationService,
    private instituteService: InstituteService
  ) {}

  ngOnInit(): void {
    this.loadStatusOptions();
    if (this.studentId) {
      this.loadApplications();
    }
  }

  /** Load Application + Offer status labels from the API (shared with operations portal). */
  private loadStatusOptions(): void {
    this.studentDirectApplicationService.getStatusOptions().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        const data = response?.result ?? response ?? {};
        const appStatuses: { label: string; value: number }[] = data.applicationStatuses ?? [];
        const offerStatuses: { label: string; value: number }[] = data.offerStatuses ?? [];
        this.statusLabelMap = new Map(appStatuses.map(o => [o.value, o.label]));
        this.offerLabelMap = new Map(offerStatuses.map(o => [o.value, o.label]));
      },
      error: () => {
        this.statusLabelMap = new Map();
        this.offerLabelMap = new Map();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentId'] && !changes['studentId'].firstChange && this.studentId) {
      this.loadApplications();
    } else if (changes['studentId'] && changes['studentId'].firstChange && this.studentId) {
      // If studentId was already available immediately, ngOnInit might have handled it, 
      // but to be safe and avoid double calls if both fire, we can let ngOnInit handle first run
      // unless ngOnInit hasn't run yet. Angular runs ngOnChanges before ngOnInit.
      // So if firstChange and has value, we can just wait for ngOnInit.
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadApplications(): void {
    this.isLoading = true;
    this.studentDirectApplicationService.getByStudentId({ studentId: this.studentId }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: any) => {
        const list: any[] = Array.isArray(response) ? response : (response?.result ?? []);

        this.applications = list.map(item => ({
          id: item.id,
          instituteId: String(item.instituteId ?? ''),
          instituteName: item.instituteName ?? String(item.instituteId ?? ''),
          instituteLogo: item.instituteLogo ?? null,
          courseLevel: item.courseLevel ?? null,
          courseLevelName: item.courseLevelName ?? null,
          courseName: item.courseName ?? null,
          countryName: item.countryName ?? null,
          inTakeYear: item.inTakeYear ?? null,
          status: item.status ?? 0,
          offerStatus: item.offerStatus ?? null,
          milestones: item.milestones ?? []
        }));

        this.resolveInstituteNames();
        this.applicationsLoaded.emit(this.applications.length);
        this.isLoading = false;
      },
      error: () => {
        this.applications = [];
        this.applicationsLoaded.emit(0);
        this.isLoading = false;
      }
    });
  }

  private resolveInstituteNames(): void {
    const uniqueIds = Array.from(new Set(this.applications.map(a => a.instituteId).filter(Boolean)));
    if (uniqueIds.length === 0) return;

    forkJoin(
      uniqueIds.map(id =>
        this.instituteService.getInstitute(id).pipe(catchError(() => of(null)))
      )
    ).pipe(takeUntil(this.destroy$)).subscribe(institutes => {
      const nameById = new Map<string, string>();
      const countryById = new Map<string, string>();
      const logoById = new Map<string, string>();

      institutes.forEach((inst: any, idx) => {
        const name = inst?.name ?? inst?.instituteName ?? inst?.result?.name;
        if (name) nameById.set(uniqueIds[idx], name);

        const country = inst?.country ?? inst?.countryName ?? inst?.result?.country;
        if (country) countryById.set(uniqueIds[idx], country);

        const logo = inst?.logo ?? inst?.result?.logo;
        if (logo) logoById.set(uniqueIds[idx], logo);
      });

      this.applications = this.applications.map(a => ({
        ...a,
        instituteName: nameById.get(a.instituteId) ?? a.instituteName,
        countryName: a.countryName || countryById.get(a.instituteId) || null,
        instituteLogo: logoById.get(a.instituteId) ?? a.instituteLogo ?? null
      }));
    });
  }

  /** Maps a raw status value to its phase (1-4), matching the operations portal ranges. */
  private phaseOf(status: number): number {
    if (status > 15) return 4;
    if (status > 9) return 3;
    if (status > 5) return 2;
    return 1;
  }

  /** Applications belonging to the currently selected phase tab. */
  get filteredApplications(): StudentApplicationRow[] {
    return this.applications.filter(a => this.phaseOf(a.status) === this.activePhase);
  }

  selectPhase(phase: number): void {
    this.activePhase = phase;
  }

  statusLabel(status: number): string {
    return this.statusLabelMap.get(status) ?? 'Unknown';
  }

  statusBadgeClass(status: number): string {
    const label = this.statusLabel(status).toLowerCase();
    if (label.includes('reject') || label.includes('denied')) return 'badge-red';
    if (label.includes('approve') || label.includes('completed') || label.includes('enrolled') || label.includes('offer')) return 'badge-green';
    if (label.includes('progress') || label.includes('assess') || label.includes('pending')) return 'badge-orange';
    if (label.includes('sent') || label.includes('submitted') || label.includes('received')) return 'badge-blue';
    return 'badge-gray';
  }

  getInitials(name: string): string {
    if (!name) return 'UN';
    const parts = name.trim().split(' ');
    if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  getInitialsBg(name: string): string {
    const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899'];
    const idx = (name?.charCodeAt(0) ?? 0) % colors.length;
    return colors[idx];
  }

  courseLevelLabel(level?: number | null): string {
    if (level === null || level === undefined) return '-';
    return CourseLevelEnumDescriptions[level as CourseLevelEnum] ?? '-';
  }

  onLogoError(event: Event, app: StudentApplicationRow): void {
    app.instituteLogo = null;
  }

  offerStatusLabel(offerStatus?: number | null): string {
    if (offerStatus === null || offerStatus === undefined) return '—';
    return this.offerLabelMap.get(offerStatus) ?? '—';
  }

  offerBadgeClass(offerStatus?: number | null): string {
    switch (offerStatus) {
      case 1: return 'badge-red';
      case 2: return 'badge-green';
      case 0: return 'badge-orange';
      default: return '';
    }
  }

  hasHistory(app: StudentApplicationRow): boolean {
    return !!app.milestones && app.milestones.length > 0;
  }

  openHistory(app: StudentApplicationRow): void {
    this.historyDialog.open(app);
  }

  // statusDescription(status: number): string {
  //   const descriptions: Record<number, string> = {
  //     0: 'Pending payment to proceed',
  //     1: 'Under review by operations',
  //     2: 'Applied to the university',
  //     3: 'Application sent to university',
  //     4: 'Application completed',
  //     5: 'Under assessment',
  //     6: 'Conditional offer received',
  //     7: 'Application rejected',
  //     8: 'Document uploaded',
  //     9: 'Enrolment fees paid',
  //     10: 'Unconditional offer received',
  //     11: 'CAS issued – UK',
  //     12: 'Form I-20 issued – US',
  //     13: 'CoE issued – Australia',
  //     14: 'LOA + PAL – Canada',
  //     15: 'Visa support letter – Ireland',
  //     16: 'Visa applied',
  //     17: 'Visa approved',
  //     18: 'Visa rejected',
  //     19: 'Insurance paid',
  //     20: 'Accommodation booked',
  //     21: 'Assessing loan application',
  //     22: 'Loan approved',
  //     23: 'Enrolled and ready to fly'
  //   };
  //   return descriptions[status] ?? '';
  // }
}

